import type { IEventChannel } from "../model/Models";
import type Util from "./util";

const es = global.es; // TODO: Remove dependency!!!

// skip these unless req'd (or logs get swamped)
const IGNORE_LIST = ["onLoadStart", "onLoadFinish"];

export default class EventChannel implements IEventChannel {
    private readonly util: Util;
    private readonly validEventTypes: object;
    private readonly channelLabel: string;

    private listeners: object | null;
    private enabled: boolean;


    /**
     * Channel for events, allowing listeners to be added and events to be fired.
     * 
     * @param util 
     * @param validEventTypes map of valid event types
     * @param channelLabel label used when logging debugging events. Can be useful when there are multiple channels with save event names e.g. onUpdate, onDestroy
     */
    constructor(util: Util, validEventTypes: object, channelLabel: string) {
        if (!validEventTypes) {
            throw new Error("Cannot instantiate EventChannel without specifying validEventTypes");
        }

        if (!channelLabel) {
            throw new Error("Cannot instantiate EventChannel without specifying channelLabel");
        }

        this.util = util;
        this.validEventTypes = validEventTypes;
        this.channelLabel = channelLabel;

        this.listeners = {};
        this.enabled = true;

        if (util.EventChannel_logging) {
            es.log.info("Created EventChannel", "channelLabel=[", (channelLabel || "(none given)") + "]", "validEventTypes=[", validEventTypes, "]");
        }
    }

    /**
     * @function ? Setup a listener
     * @param eventType the type of event on which to listen
     * @param callback callback function
     * @param invocations max number of times the callback will be invoked
     * @param owner - the object which will be referenced by the 'this' pointer
     * @return this
     */
    setListener(eventType: string, callback: Function, invocations?: number, owner?: any): EventChannel {
        if (this.listeners) {
            if (!eventType) {
                es.error.handleJSError(new Error("Error setting event listener on channel " + this.channelLabel + ": NoEventType"));
            }
            if (!callback) {
                es.error.handleJSError(new Error("Error setting event listener on channel " + this.channelLabel + ": NoCallback"));
            }
            if (this.validEventTypes && (!this.validEventTypes[eventType])) {
                console.error("---------------------------------------------");
                console.error("UnknownEventType", "channelLabel:[" + this.channelLabel + "], eventType:[" + eventType + "]");
                console.error("validEventTypes for this channel are:", this.validEventTypes);
                console.error("The listener/callback set for this unknown event is:", callback);
                console.error("---------------------------------------------");

                es.error.handleJSError(new Error("Listener added for unknown event. channelLabel:[" + this.channelLabel + "], eventType:[" + eventType + "]"));
            }
            if ((!invocations) && (invocations !== 0)) {
                invocations = -1;
            }

            const typeListeners = this.listeners[eventType];
            if (typeListeners) {
                for (let i = typeListeners.length; i-- > 0;) {
                    const listener = typeListeners[i];
                    if ((listener.callback === callback) && (listener.owner === owner)) { // callback found...
                        if (invocations) {
                            listener.invocations = invocations; // listener already existed - update number of invocations
                        } else {
                            let newListeners = typeListeners.slice(0,i);
                            newListeners.push.apply(newListeners, typeListeners.slice(i+1));
                            this.listeners[eventType] = newListeners;
                        }
                        return this; // done...
                    }
                }
                
                if (invocations) { //Listener was not found, add it if there are invocations...
                    let newListeners = [{ // New listener goes at start of array (This is because we iterate through them in reverse order)
                        callback : callback,
                        owner : owner,
                        invocations : invocations
                    }];
                    newListeners.push.apply(newListeners, typeListeners); // existing listeners added next
                    this.listeners[eventType] = newListeners;
                }
            } else if (invocations) { // If there are invocations, add callback
                this.listeners[eventType] = [{
                    callback: callback,
                    owner: owner,
                    invocations: invocations
                }];
            }
        }

        return this;
    }
    
    /**
     * Add a listener
     * 
     * @param eventType the type of event on which to listen
     * @param callback callback function
     * @param owner - the object which will be referenced by the 'this' pointer
     * 
     * @returns this
     */
    addListener(eventType: string, callback: Function, owner?: any): EventChannel {
        // if(this.util.EventChannel_logging) {
        //     es.log.log("addListener", channelLabel + "#" + eventType, callback);
        // }
        return this.setListener(eventType, callback, -1, owner);
    }
    
    /**
     * Remove a listener
     * 
     * @param eventType the type of event on which to listen
     * @param callback callback function
     * @param owner - the object which will be referenced by the 'this' pointer
     * 
     * @returns this
     */
    removeListener(eventType: string, callback: Function, owner = undefined): EventChannel {
        // if(this.util.EventChannel_logging) {
        //     es.log.log("removeListener", channelLabel + "#" + eventType, callback);
        // }
        return this.setListener(eventType, callback, 0, owner);
    }
    
    /**
     * get the number of listeners for a given event type
     * 
     * @param eventType the type of event on which to listen
     * 
     * @returns number of listeners
     */
    getListenersSize(eventType: string): number {
        if (!this.listeners || !this.listeners[eventType]) {
            return 0;
        }
        
        return this.listeners[eventType].length;
    }
    
    /**
     * Add a listener for a single invocation
     * 
     * @param eventType the type of event on which to listen
     * @param callback callback function
     * @param owner - the object which will be referenced by the 'this' pointer
     * 
     * @returns this
     */
    addOnce(eventType: string, callback: Function, owner?: any): EventChannel {
        return this.setListener(eventType, callback, 1, owner);
    }
    
    /**
     * @function ? Fire an event - takes extra arguments and passes them on to the callback function.
     * If a callback results in changes to the queue (callbacks added / removed),
     * this will not be manifested until after the callbacks have all been invoked (So new
     * callbacks will not be invoked, and removed callbacks will still be invoked that last time.)
     * @param eventType the type of event to fire
     * @param arg1 first argument to pass to callback function - accepts 0-n args
     * @param arg2 second argument to pass to callback function - accepts 0-n args
     * @return promise to be resolved when all callbacks are resolved (if they return a promise)
     */
    fireEvent(eventType: string, arg1?: any, arg2?: any): Promise<void> {
        if (!this.enabled){
            return Promise.resolve();
        }

        if (this.listeners == null) {
            console.warn("Attempted to use destroyed eventChannel [" + this.channelLabel + "]... ignoring the request!");
            return Promise.resolve();
        }
        
        if ( (typeof eventType !== "string") ||
                (this.validEventTypes && (!this.validEventTypes[eventType])) ) {

            es.error.handleJSError(new Error("UnknownEventType: " + this.channelLabel + "#" + eventType));
        }
        
        //Get arguments to pass to callbacks
        const args = [];
        for (let a = 0; a < arguments.length; a++) {
            args[a-1] = arguments[a];
        }

        const eventTypeListeners = this.listeners[eventType];

        if (this.util.EventChannel_logging) {
            if(!IGNORE_LIST.includes(eventType)){
                es.log.log("fire", this.channelLabel + "#" + eventType, "(" + (eventTypeListeners ? eventTypeListeners.length : "no") + " listeners)", arg1, arg2);
            }
        }

        const promises = [];
        if (eventTypeListeners) {
            let listenerCount = 1;
            for (let i = eventTypeListeners.length; i-- > 0;) {
                const listener = eventTypeListeners[i];
                const owner = listener.owner;
                const callback = listener.callback;

                if(this.util.EventChannel_loggingListeners){
                    es.log.log("  listener ", this.channelLabel + "#" + eventType, (listenerCount++) + "/" + (eventTypeListeners.length), "\n  " + callback);
                }

                try {
                    let p = callback.apply(owner || window, args);
                    if (p) {
                        promises.push(p);
                    }
                } catch(err){
                    console.error("Error in callback fn for event [" + this.channelLabel + "#" + eventType + "]", err);
                }

                listener.invocations--;
                if (!listener.invocations) {
                    this.setListener(eventType, callback, 0, owner);
                }
            }
        } else if (this.validEventTypes && (!this.validEventTypes[eventType])) {
            es.error.handleJSError(new Error("UnknownEventType: " + this.channelLabel + "#" + eventType));
        }

        return Promise.allSettled(promises).then(() => Promise.resolve()); // wait for all promises to resolve and always resolve (even if some were rejected)
    }

    /**
     * @function ? Dispose of this channel. After this method has been called,
     * the channel will no longer be usable
     */
    destroy() {
        this.listeners = null;
    }

    /**
     * @function ? disables all events from being fired until re-enabled,
     * the channel will not fire any events.
     */
    disableListeners() {
        this.enabled = false;
    }

    /**
     * enables back all firing events,
     * the channel will not fire any events.
     */
    enableListeners() {
        this.enabled = true;
    }
}
